import { useState } from "react";
import { FaGithub, FaGoogle } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";
import { useAuthActions, usePendingCredentialInfo } from "../../core_stores/useAuthStore";
import { colors } from "../../design/tokens";
import { AuthModalLayout } from "./components/AuthModalLayout";

export function AuthModal({
  isShown,
  canClose,
  additionalBtn,
  onClose,
}: {
  isShown: boolean;
  canClose: boolean;
  additionalBtn?: {
    label: string;
    onClick: () => void;
  };
  onClose: () => void;
}) {
  const pendingCredentialInfo = usePendingCredentialInfo();

  function handleClose() {
    onClose();
  }

  if (!isShown) {
    return null;
  }

  return (
    <AuthModalLayout canClose={canClose} onClose={handleClose}>
      <div className="w-full h-full flex items-center justify-center">
        {pendingCredentialInfo ? (
          <LinkAccountsInfo
            pendingCredentialInfo={pendingCredentialInfo}
            onSignedIn={() => onClose()}
          />
        ) : (
          <Info
            onSignedIn={() => onClose()}
            additionalBtn={additionalBtn}
          />
        )}
      </div>
    </AuthModalLayout>
  );
}

function Info({
  additionalBtn,
  onSignedIn,
}: {
  additionalBtn?: {
    label: string;
    onClick: () => void;
  };
  onSignedIn: () => void;
}) {
  const { signInWithGoogle, signInWithGithub } = useAuthActions();
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingGithub, setIsLoadingGithub] = useState(false);

  const handleGoogleSignIn = async () => {
    if (isLoadingGoogle || isLoadingGithub) return;

    setIsLoadingGoogle(true);
    try {
      const success = await signInWithGoogle();
      if (success) {
        onSignedIn();
      }
      // If not successful, linking flow will be shown (pendingCredentialInfo is set)
    } catch (error) {
      console.error("Google sign in failed:", error);
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const handleGithubSignIn = async () => {
    if (isLoadingGoogle || isLoadingGithub) return;

    setIsLoadingGithub(true);
    try {
      const success = await signInWithGithub();
      if (success) {
        onSignedIn();
      }
      // If not successful, linking flow will be shown (pendingCredentialInfo is set)
    } catch (error) {
      console.error("GitHub sign in failed:", error);
    } finally {
      setIsLoadingGithub(false);
    }
  };

  return (
    <div
      className="flex w-full flex-col items-center justify-center
      h-full px-[5%] py-[10%] gap-10
    text-light-text-secondary dark:text-dark-text-secondary"
    >
      <span className="text-3xl font-[700] text-center text-light-text dark:text-dark-text">
        Sign in or create free account
      </span>
      <span className="text-center font-[600]">
        In order to proceed you need to be signed in.
      </span>
      <div className="flex flex-col gap-4 w-[258px]">
        <button
          id="auth-modal-google-sign-in"
          onClick={handleGoogleSignIn}
          disabled={isLoadingGoogle || isLoadingGithub}
          className={`w-full h-[40px] bg-primary-blue disabled:opacity-50 disabled:cursor-not-allowed
            text-white font-medium rounded-full flex items-center justify-center gap-3
            transition-all duration-200 ${colors.hover.opacity}`}
        >
          {isLoadingGoogle ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <FaGoogle />
              Continue with Google
            </>
          )}
        </button>
        <button
          id="auth-modal-github-sign-in"
          onClick={handleGithubSignIn}
          disabled={isLoadingGoogle || isLoadingGithub}
          className={`w-full h-[40px] bg-[#24292e] dark:bg-[#f0f6fc] disabled:opacity-50 disabled:cursor-not-allowed
            text-white dark:text-[#24292e] font-medium rounded-full flex items-center justify-center gap-3
            transition-all duration-200 ${colors.hover.opacity}`}
        >
          {isLoadingGithub ? (
            <div className="w-5 h-5 border-2 border-white dark:border-[#24292e] border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <FaGithub />
              Continue with GitHub
            </>
          )}
        </button>
        {additionalBtn && (
          <button
            onClick={additionalBtn.onClick}
            className={`w-full h-[40px] bg-primary-blue disabled:opacity-50 disabled:cursor-not-allowed
            text-white font-medium rounded-full flex items-center justify-center gap-3
            transition-all duration-200 ${colors.hover.opacity}`}
          >
            {additionalBtn.label}
          </button>
        )}
      </div>
    </div>
  );
}

// Component shown when account linking is needed
function LinkAccountsInfo({
  pendingCredentialInfo,
  onSignedIn,
}: {
  pendingCredentialInfo: {
    credential: unknown;
    email: string;
    pendingProvider: string;
  };
  onSignedIn: () => void;
}) {
  const { signInWithGoogleAndLink, signInWithGithubAndLink, clearPendingCredential } = useAuthActions();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pendingProviderName = pendingCredentialInfo.pendingProvider === 'github.com' ? 'GitHub' : 'Google';

  // Show all providers except the one user tried to sign in with
  const showGoogle = pendingCredentialInfo.pendingProvider !== 'google.com';
  const showGithub = pendingCredentialInfo.pendingProvider !== 'github.com';

  const handleSignInAndLink = async (provider: 'google' | 'github') => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);
    try {
      if (provider === 'google') {
        await signInWithGoogleAndLink();
      } else {
        await signInWithGithubAndLink();
      }
      onSignedIn();
    } catch (err) {
      console.error("Account linking failed:", err);
      setError("Failed to link accounts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    clearPendingCredential();
  };

  return (
    <div
      className="flex w-full flex-col items-center justify-center
      h-full px-[5%] py-[10%] gap-6
      text-light-text-secondary dark:text-dark-text-secondary"
    >
      <span className="text-2xl font-[700] text-center text-light-text dark:text-dark-text">
        Link your accounts
      </span>
      <div className="text-center font-[500] space-y-2 max-w-[320px]">
        <p>
          An account with <span className="font-semibold text-light-text dark:text-dark-text">{pendingCredentialInfo.email}</span> already exists.
        </p>
        <p className="text-sm">
          Sign in with your existing account to link {pendingProviderName}.
        </p>
      </div>

      {error && (
        <div className="text-red-500 text-sm text-center">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-3 w-[258px]">
        {showGoogle && (
          <button
            onClick={() => handleSignInAndLink('google')}
            disabled={isLoading}
            className={`w-full h-[40px] bg-primary-blue disabled:opacity-50 disabled:cursor-not-allowed
              text-white font-medium rounded-full flex items-center justify-center gap-3
              transition-all duration-200 ${colors.hover.opacity}`}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <FaGoogle />
                Sign in with Google to link
              </>
            )}
          </button>
        )}
        {showGithub && (
          <button
            onClick={() => handleSignInAndLink('github')}
            disabled={isLoading}
            className={`w-full h-[40px] bg-[#24292e] dark:bg-[#f0f6fc] disabled:opacity-50 disabled:cursor-not-allowed
              text-white dark:text-[#24292e] font-medium rounded-full flex items-center justify-center gap-3
              transition-all duration-200 ${colors.hover.opacity}`}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white dark:border-[#24292e] border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <FaGithub />
                Sign in with GitHub to link
              </>
            )}
          </button>
        )}

        <button
          onClick={handleBack}
          disabled={isLoading}
          className="w-full h-[40px] bg-transparent border border-light-border dark:border-dark-border
            text-light-text dark:text-dark-text font-medium rounded-full flex items-center justify-center gap-2
            transition-all duration-200 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary disabled:opacity-50"
        >
          <IoArrowBack />
          Back
        </button>
      </div>

      <p className="text-xs text-center max-w-[280px] opacity-70">
        After linking, you can use either {pendingProviderName} or your existing account to sign in.
      </p>
    </div>
  );
}
